import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  useTSQCompanyList,
  useTSMDeactivateCompany,
  useTSMReactivateCompany,
} from "../../hooks/useTSEntities";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import {
  CompanyFullGroup,
  VendorFull,
  CustomerFull,
  CompanyType,
} from "../../types/companyTypes";

export const PageCompanyList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const curCompanyType =
    (searchParams.get("type") as CompanyType) || ("customer" as CompanyType);
  const curCompanyTypePretty = curCompanyType
    .charAt(0)
    .toUpperCase()
    .concat(curCompanyType.slice(1));

  // Local state for UI controls
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [searchFilters, setSearchFilters] = useState<ListRequestFilter[][]>([
    [],
  ]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [error, setError] = useState<string | null>(null);

  // Sorting
  const [orderBy, setOrderBy] = useState<string>(
    curCompanyType === "vendor" ? "name" : "code",
  );
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // TanStack Query hooks
  const {
    data: companiesResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQCompanyList(
    {
      skip: -1,
      limit: -1,
      active_status: statusFilter,
      filters: searchFilters,
    },
    curCompanyType,
  );

  const deactivateEntityMutation = useTSMDeactivateCompany();
  const reactivateEntityMutation = useTSMReactivateCompany();

  const [companies, setCompanies] = useState<VendorFull[] | CustomerFull[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    if (companiesResponse?.items) {
      setCompanies(companiesResponse.items);
      setTotalCount(companiesResponse.total);
    }
  }, [companiesResponse]);

  // Handle mutations
  const handleDeactivate = async (id: string, company_type: CompanyType) => {
    if (
      !window.confirm(
        `Are you sure you want to deactivate this ${company_type}?`,
      )
    ) {
      return;
    }
    try {
      await deactivateEntityMutation.mutateAsync({ id, company_type });
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate entity:", err);
      setError(err.response?.data?.detail || "Failed to deactivate entity");
    }
  };

  const handleReactivate = async (id: string, company_type: CompanyType) => {
    try {
      await reactivateEntityMutation.mutateAsync({ id, company_type });
      setError(null);
    } catch (err: any) {
      console.error("Failed to restore entity:", err);
      setError(err.response?.data?.detail || "Failed to restore entity");
    }
  };

  // Pagination handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Handle sorting
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Get sortable value from item
  const getSortValue = (company: CompanyFullGroup, property: string): any => {
    switch (property) {
      case "name":
        return company.name;
      case "code":
        return company.code;
      case "default_payment_terms":
        return company.default_payment_terms;
      case "status":
        return company.is_record_active ? "active" : "inactive";
      default:
        return "";
    }
  };

  // Filter and sort items (client-side filtering for search and category)
  const filteredAndSortedCompanies = useMemo(() => {
    const filtered = (() => {
      switch (curCompanyType) {
        case "customer":
          return (companies as CustomerFull[]).filter((cust) => {
            return (
              searchTerm === "" ||
              cust.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
              cust.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
          });
        case "vendor":
          return (companies as VendorFull[]).filter((vend) => {
            return (
              searchTerm === "" ||
              vend.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
              vend.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
          });
      }
    })();

    // Then sort
    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [companies, searchTerm, orderBy, order, curCompanyType, pagination]);

  // Show loading spinner only on initial load
  if (isLoading && companies.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {curCompanyTypePretty.concat("s")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            navigate(`/companies/form?mode=create&type=${curCompanyType}`)
          }
        >
          {`Add ${curCompanyTypePretty}`}
        </Button>
      </Box>

      {/* Error handling */}
      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An error occurred"}
        </Alert>
      )}

      {/* Loading indicator for background updates */}
      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search"
            placeholder={`Search by name${
              ["customer", "vendor"].includes(curCompanyType) ? " or code" : ""
            }...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Items Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "code" ? order : false}>
                <TableSortLabel
                  active={orderBy === "code"}
                  direction={orderBy === "code" ? order : "asc"}
                  onClick={() => handleSort("code")}
                >
                  Code
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "status" ? order : false}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {`No ${curCompanyTypePretty.concat("s")} Found`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedCompanies.map((entity) => {
                return (
                  <TableRow key={entity.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {entity.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{entity.code}</TableCell>
                    <TableCell>
                      <Chip
                        label={entity.is_record_active ? "Active" : "Inactive"}
                        color={entity.is_record_active ? "success" : "default"}
                        size="small"
                        clickable={false}
                        onClick={() => {}}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/companies/form?mode=view&id=${entity.id}&type=${curCompanyType}`,
                              )
                            }
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Item">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/companies/form?mode=edit&id=${entity.id}&type=${curCompanyType}`,
                              )
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        {!entity.is_record_active ? (
                          <Tooltip title={`Reactivate ${curCompanyTypePretty}`}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleReactivate(entity.id, curCompanyType)
                              }
                              color="success"
                              disabled={reactivateEntityMutation.isPending}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Deactivate ${curCompanyTypePretty}`}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeactivate(entity.id, curCompanyType)
                              }
                              color="error"
                              disabled={deactivateEntityMutation.isPending}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
